import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Users, CreditCard, DollarSign, AlertTriangle, MessageSquare, TrendingUp,
  Activity, ChevronRight, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#9F7AEA', '#ED8936', '#4FD1C5', '#FC8181'];

export default function SaaSOverview() {
  const [kpis, setKpis] = useState(null);
  const [tenantGrowth, setTenantGrowth] = useState([]);
  const [templateUsage, setTemplateUsage] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [kpisRes, growthRes, templateRes, activityRes] = await Promise.all([
        axios.get(`${API}/saas/kpis`),
        axios.get(`${API}/saas/tenant-growth`),
        axios.get(`${API}/saas/template-usage`),
        axios.get(`${API}/saas/recent-activity`)
      ]);
      setKpis(kpisRes.data);
      setTenantGrowth(growthRes.data);
      setTemplateUsage(templateRes.data);
      setRecentActivity(activityRes.data);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="saas-loading">Loading dashboard...</div>;

  const pieData = templateUsage.map(t => ({ name: t.template_name, value: t.total_tenants }));
  const subBreakdown = [
    { name: 'Active', value: kpis?.active_subscriptions || 0, color: '#38A169' },
    { name: 'Trialing', value: kpis?.trialing_subscriptions || 0, color: '#9F7AEA' },
    { name: 'Past Due', value: kpis?.past_due_subscriptions || 0, color: '#E53E3E' },
    { name: 'Canceled', value: kpis?.canceled_subscriptions || 0, color: '#718096' },
  ].filter(d => d.value > 0);

  return (
    <div className="saas-overview" data-testid="saas-overview">
      {/* KPI Cards */}
      <div className="saas-kpi-grid">
        <div className="saas-kpi-card" data-testid="kpi-total-tenants">
          <div className="kpi-icon" style={{ background: '#3182CE20', color: '#3182CE' }}><Users size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Total Tenants</span>
            <span className="kpi-value">{kpis?.total_tenants || 0}</span>
          </div>
          <div className="kpi-trend up"><TrendingUp size={14} /><span>+{kpis?.new_tenants_30d || 0} this month</span></div>
        </div>

        <div className="saas-kpi-card" data-testid="kpi-active-subs">
          <div className="kpi-icon" style={{ background: '#38A16920', color: '#38A169' }}><CreditCard size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Active Subscriptions</span>
            <span className="kpi-value">{kpis?.active_subscriptions || 0}</span>
          </div>
          <div className="kpi-meta"><span>{kpis?.trialing_subscriptions || 0} trialing</span></div>
        </div>

        <div className="saas-kpi-card" data-testid="kpi-mrr">
          <div className="kpi-icon" style={{ background: '#D69E2E20', color: '#D69E2E' }}><DollarSign size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Monthly Recurring Revenue</span>
            <span className="kpi-value">${(kpis?.mrr || 0).toLocaleString()}</span>
          </div>
        </div>

        <div className="saas-kpi-card" data-testid="kpi-trial">
          <div className="kpi-icon" style={{ background: '#9F7AEA20', color: '#9F7AEA' }}><Activity size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Trial Tenants</span>
            <span className="kpi-value">{kpis?.trial_tenants || 0}</span>
          </div>
        </div>

        <div className="saas-kpi-card" data-testid="kpi-errors">
          <div className="kpi-icon" style={{ background: '#E53E3E20', color: '#E53E3E' }}><AlertTriangle size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Errors Today</span>
            <span className="kpi-value">{kpis?.errors_today || 0}</span>
          </div>
          <div className="kpi-meta error"><span>{kpis?.total_unresolved_errors || 0} unresolved</span></div>
        </div>

        <div className="saas-kpi-card" data-testid="kpi-tickets">
          <div className="kpi-icon" style={{ background: '#ED893620', color: '#ED8936' }}><MessageSquare size={24} /></div>
          <div className="kpi-content">
            <span className="kpi-label">Open Support Tickets</span>
            <span className="kpi-value">{kpis?.open_support_tickets || 0}</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="saas-widgets-row">
        <div className="saas-widget">
          <div className="widget-header">
            <h3>Tenant Growth</h3>
            <button className="widget-refresh" onClick={fetchData}><RefreshCw size={16} /></button>
          </div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tenantGrowth.slice(-6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3d" />
                <XAxis dataKey="month" stroke="#888" tick={{ fontSize: 12 }} tickFormatter={(v) => v.split(' ')[0]} />
                <YAxis stroke="#888" allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#232326', border: '1px solid #3a3a3d', borderRadius: 8, color: '#e5e5e5' }} />
                <Bar dataKey="tenants" fill="#3182CE" radius={[4, 4, 0, 0]} name="New Tenants" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="saas-widget">
          <div className="widget-header">
            <h3>Template Usage</h3>
            <Link to="/admin/saas/templates" className="widget-link">View All <ChevronRight size={16} /></Link>
          </div>
          <div style={{ height: 280 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#232326', border: '1px solid #3a3a3d', borderRadius: 8, color: '#e5e5e5' }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state-small">No template usage data</div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Breakdown */}
      <div className="saas-widgets-row">
        <div className="saas-widget">
          <div className="widget-header"><h3>Subscription Status</h3></div>
          <div style={{ height: 220 }}>
            {subBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={subBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label>
                    {subBreakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#232326', border: '1px solid #3a3a3d', borderRadius: 8, color: '#e5e5e5' }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state-small">No subscription data</div>
            )}
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="saas-widget">
          <div className="widget-header">
            <h3>Recent Tenants</h3>
            <Link to="/admin/saas/tenants" className="widget-link">View All <ChevronRight size={16} /></Link>
          </div>
          <div className="recent-list">
            {recentActivity?.recent_tenants?.map((tenant, idx) => (
              <div key={idx} className="recent-item">
                <div className="recent-icon tenant">{tenant.name.charAt(0)}</div>
                <div className="recent-info">
                  <strong>{tenant.name}</strong>
                  <span>{tenant.template_slug}</span>
                </div>
                <span className="recent-time">{new Date(tenant.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {(!recentActivity?.recent_tenants || recentActivity.recent_tenants.length === 0) && (
              <div className="empty-state-small">No recent tenants</div>
            )}
          </div>
        </div>

        {/* Recent Errors */}
        <div className="saas-widget">
          <div className="widget-header">
            <h3>Recent Errors</h3>
            <Link to="/admin/saas/errors" className="widget-link">View All <ChevronRight size={16} /></Link>
          </div>
          <div className="recent-list">
            {recentActivity?.recent_errors?.map((error, idx) => (
              <div key={idx} className="recent-item">
                <div className={`recent-icon error ${error.severity}`}><AlertTriangle size={14} /></div>
                <div className="recent-info">
                  <strong>{error.message?.substring(0, 40)}...</strong>
                  <span>{error.module}</span>
                </div>
                <span className={`severity-badge ${error.severity}`}>{error.severity}</span>
              </div>
            ))}
            {(!recentActivity?.recent_errors || recentActivity.recent_errors.length === 0) && (
              <div className="empty-state-small">No recent errors</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
